import React from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * FollowUpSuggestions — chip row of contextual follow-up questions.
 *
 * Renders 1-3 chips beneath an assistant message. Clicking a chip
 * sends that suggestion as the next user message via `onSelect`.
 *
 * Props
 *   suggestions  — string[]
 *   onSelect     — (suggestion: string) => void
 *   disabled     — boolean (e.g. while a previous message is streaming)
 */
export const FollowUpSuggestions = ({ suggestions = [], onSelect, disabled = false }) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';

  if (!suggestions?.length) return null;

  // Visual anchoring (May-21 UX audit): the chip row reads as a
  // secondary affordance directly *belonging* to the assistant
  // message above. A subtle inline-start border + indent ties the
  // chips to the preceding bubble so they no longer float as a
  // separate block between turns. Font is small + muted so the
  // chips don't compete with the answer itself.
  return (
    <div
      className={cn(
        'mt-3 flex flex-col gap-1.5',
        // 2 px accent border on the leading edge ties this row to the
        // assistant message above. Use start/end logical properties so
        // the line lands on the correct side in RTL too.
        'border-s-2 border-black/[0.08] ps-3',
        isArabic && 'font-arabic',
      )}
      data-testid="follow-up-suggestions"
    >
      <div
        className={cn(
          'flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#B5B0A5]',
          isArabic && 'flex-row-reverse',
        )}
      >
        <Sparkles className="w-2.5 h-2.5" strokeWidth={1.8} />
        <span>{isArabic ? 'متابعة المحادثة' : 'Continue the conversation'}</span>
      </div>
      <div className={cn('flex flex-wrap gap-1.5', isArabic && 'flex-row-reverse')}>
        {suggestions.slice(0, 3).map((suggestion, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => onSelect?.(suggestion)}
            disabled={disabled}
            data-testid={`follow-up-suggestion-${idx}`}
            className={cn(
              'rounded-full border border-black/[0.08] bg-transparent px-2.5 py-1 text-xs',
              'text-[#6B6B6B] hover:text-[#1A1A1A] hover:border-[#7C5E3C]/35 hover:bg-[#FAF7F2]',
              'transition-colors leading-tight',
              isArabic && 'text-right',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>
  );
};

export default FollowUpSuggestions;
